import { useCallback, useState } from 'react';

import { Api } from 'src/models/Api.model';
import { EntityCriteriaOperatorEnum } from 'src/models/GalenData/EntityCriteria.model';
import { PageOfUser } from 'src/models/GalenData/PageOfUser.model';
import { UserRoleEnum } from 'src/models/GalenData/UserRole.model';
import { getLocaleCode, Language } from 'src/models/Language.model';

import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';

export default function useGetUsers() {
  const [users, setUsers] = useState<PageOfUser>({});
  const [isGettingUsers, setIsGettingUsers] = useState(false);

  const { tCommon, language } = useUiTranslation();

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const handleGetUsers = useCallback(
    async (
      pageSize?: number,
      pageNumber: number = 0,
      searchValues: { emailAddress: string; name: string } = {
        emailAddress: '',
        name: '',
      },
    ) => {
      try {
        setIsGettingUsers(true);

        const results = await Api.remote._get_users_advanced(
          [
            {
              key: 'role',
              operator: EntityCriteriaOperatorEnum.NotEqual,
              value: UserRoleEnum.Patient,
            },
            {
              key: 'emailAddress',
              operator: EntityCriteriaOperatorEnum.Like,
              value: searchValues.emailAddress.trim(),
            },
            {
              key: 'name',
              operator: EntityCriteriaOperatorEnum.Like,
              value: searchValues.name.trim(),
            },
          ],
          {
            pageSize,
            pageNumber,
            sortBy: 'createdOn',
            sortOrder: 'DESC',
          },
        );

        const usersData = {
          content: results.data.content,
          totalElements: results.data.totalElements,
          totalPages: results.data.totalPages,
          size: pageSize,
          number: pageNumber,
        };

        setUsers(usersData);

        return usersData;
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: `${tCommon('message.error.failedToLoadUsers')} ${errorMessage}`,
          locale: getLocaleCode(language as Language),
          type: 'error',
        });

        throw error;
      } finally {
        setIsGettingUsers(false);
      }
    },
    [handleHttpRequestError, language, tCommon],
  );

  return { users, handleGetUsers, isGettingUsers };
}
